import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Tám lớp CSS **viết sẵn**, không dựng bằng template string.
 *
 * Tailwind quét mã nguồn để biết cần sinh lớp nào. `bg-tag-${index}` là một
 * chuỗi được ghép lúc chạy nên nó không có trong mã nguồn, và lớp đó **không
 * được sinh ra** — nhãn hiện đúng chữ, đúng bố cục, và trong suốt. Đây là kiểu
 * lỗi chỉ thấy trên trình duyệt, không thấy ở test.
 */
const PALETTE = [
  { chip: 'bg-tag-1/12 text-tag-1 ring-tag-1/25', text: 'text-tag-1' },
  { chip: 'bg-tag-2/12 text-tag-2 ring-tag-2/25', text: 'text-tag-2' },
  { chip: 'bg-tag-3/12 text-tag-3 ring-tag-3/25', text: 'text-tag-3' },
  { chip: 'bg-tag-4/12 text-tag-4 ring-tag-4/25', text: 'text-tag-4' },
  { chip: 'bg-tag-5/12 text-tag-5 ring-tag-5/25', text: 'text-tag-5' },
  { chip: 'bg-tag-6/12 text-tag-6 ring-tag-6/25', text: 'text-tag-6' },
  { chip: 'bg-tag-7/12 text-tag-7 ring-tag-7/25', text: 'text-tag-7' },
  { chip: 'bg-tag-8/12 text-tag-8 ring-tag-8/25', text: 'text-tag-8' },
] as const;

/** Ô bảng màu của một loại việc. Băm ổn định — xem `tagClassOf`. */
function paletteOf(taskTypeId: string): (typeof PALETTE)[number] {
  let hash = 2_166_136_261;
  for (let index = 0; index < taskTypeId.length; index += 1) {
    hash = Math.imul(hash ^ taskTypeId.charCodeAt(index), 16_777_619);
  }

  return PALETTE[(hash >>> 0) % PALETTE.length]!;
}

/**
 * **Chỉ** lớp màu chữ, không nền không viền.
 *
 * Cho những chỗ không vẽ được cả cái nhãn: `<option>` của một `<select>` chỉ
 * nhận màu chữ, mọi thứ khác bị trình duyệt bỏ qua. Vẫn cùng một phép băm với
 * nhãn trên thẻ, nên "Backend" ở ô chọn và "Backend" trên thẻ là một màu.
 */
export function tagTextClassOf(taskTypeId: string): string {
  return paletteOf(taskTypeId).text;
}

/**
 * Chọn màu **ổn định** cho một loại task chưa được đặt màu.
 *
 * Băm từ `taskTypeId` chứ không lấy theo vị trí trong danh sách: cùng một loại
 * phải ra cùng một màu ở board, ở backlog và ở "Việc của tôi" — mà ba chỗ đó
 * tải ba danh sách khác nhau, thứ tự khác nhau. Lấy theo vị trí thì "Backend"
 * xanh ở màn hình này và cam ở màn hình kia, và màu thành thứ gây nhiễu chứ
 * không giúp phân biệt.
 *
 * FNV-1a, cùng thuật toán với `dataset-plan.ts`.
 */
export function tagClassOf(taskTypeId: string): string {
  return paletteOf(taskTypeId).chip;
}

interface TaskTypeTagProps {
  readonly id: string;
  readonly name: string;
  /** Màu do admin đặt (`task_type.color`). `null` = tự suy ra từ `id`. */
  readonly color?: string | null;
  readonly className?: string;
}

/**
 * Nhãn màu cho loại công việc — dùng ở board, backlog và "Việc của tôi".
 *
 * ## Vì sao màu, khi đã có chữ
 *
 * Một danh sách bốn mươi dòng mà loại việc chỉ là chữ xám thứ ba trong một
 * dòng metadata thì người đọc phải **đọc** từng dòng mới biết dòng nào là
 * Backend, dòng nào là Kiểm thử. Màu cho phép nhìn ra cụm ngay, và giữ nguyên
 * chữ bên cạnh nên nó là kênh **thứ hai**, không phải kênh duy nhất —
 * docs/frontend-guide.md §4: người mù màu vẫn đọc được nhãn.
 *
 * ## Vì sao không phải token RAG
 *
 * RAG mang ý nghĩa tốt/xấu. Dùng đỏ RAG cho một loại task là nói rằng loại đó
 * có vấn đề. Bộ `--color-tag-*` tồn tại riêng cho việc *phân biệt*.
 */
export function TaskTypeTag({ id, name, color, className }: TaskTypeTagProps): ReactNode {
  if (name.length === 0) return null;

  const base =
    'inline-flex max-w-40 items-center truncate rounded-full px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset';

  /*
    Màu chọn từ bảng 30 màu (`TASK_TYPE_COLORS`, hex) — người dùng yêu cầu
    2026-09-25 "chữ và màu nền": vẽ **nền đặc** bằng chính màu ấy, chữ trắng hay
    đen tuỳ độ sáng của nền để luôn đọc được.
  */
  const solid = color ? solidTagStyle(color) : null;
  if (solid) {
    return (
      <span className={cn(base, 'ring-0', className)} style={solid}>
        {name}
      </span>
    );
  }

  // Màu khác bảng (dữ liệu cũ, cú pháp CSS bất kỳ): chữ màu trên nền rất nhạt.
  // `color-mix` thay vì tự tính rgba vì chuỗi có thể là hex, oklch hay tên màu.
  if (color) {
    return (
      <span
        className={cn(base, className)}
        style={{
          color,
          backgroundColor: `color-mix(in oklch, ${color} 12%, transparent)`,
          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${color} 25%, transparent)`,
        }}
      >
        {name}
      </span>
    );
  }

  return <span className={cn(base, tagClassOf(id), className)}>{name}</span>;
}

/**
 * Nền đặc + chữ tương phản cho một màu hex `#rrggbb`, hoặc `null` nếu không
 * phải hex. Ngưỡng độ sáng 0.6 (sRGB tương đối): trên ngưỡng thì chữ gần đen.
 */
export function solidTagStyle(color: string): { backgroundColor: string; color: string } | null {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match) return null;
  const value = Number.parseInt(match[1]!, 16);
  const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return { backgroundColor: color, color: luminance > 0.6 ? '#111827' : '#ffffff' };
}
